import React, { useState, useMemo } from 'react';
import { Users, UserPlus, HeartHandshake, AlertCircle } from 'lucide-react';
import { Card, Input } from '../ui';
import { cn } from '../../lib/utils';

export function ChurchSmallGroupRebalancer() {
  const [congregationSize, setCongregationSize] = useState<number>(850);
  const [targetEngagementPercent, setTargetEngagementPercent] = useState<number>(60);
  const [activeLeaders, setActiveLeaders] = useState<number>(24);
  const [targetGroupSize, setTargetGroupSize] = useState<number>(12);

  const model = useMemo(() => {
    // How many people do we want in small groups?
    const targetParticipants = Math.round(congregationSize * (targetEngagementPercent / 100));
    
    // Current capacity based on active leaders
    const currentCapacity = activeLeaders * targetGroupSize;
    
    // How many total leaders do we need for the target participants?
    const requiredLeaders = Math.ceil(targetParticipants / targetGroupSize);
    
    // Leadership gap
    const leadershipGap = Math.max(0, requiredLeaders - activeLeaders);
    const capacityUtilization = currentCapacity > 0 ? (targetParticipants / currentCapacity) * 100 : 0;
    
    const isOverCapacity = capacityUtilization > 100;

    return {
      targetParticipants,
      currentCapacity,
      requiredLeaders,
      leadershipGap,
      capacityUtilization,
      isOverCapacity
    };
  }, [congregationSize, targetEngagementPercent, activeLeaders, targetGroupSize]);

  return (
    <div className="max-w-5xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-blue-700 to-indigo-900 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <HeartHandshake className="w-6 h-6 mr-3 text-blue-300" />
            Small Group & Ministry Rebalancer
          </h2>
          <p className="text-blue-100 max-w-xl">
            Ensure no one falls through the cracks. Model your small group leadership pipeline against your congregation's engagement goals to identify hidden capacity bottlenecks.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        
        {/* Controls */}
        <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
          <div>
            <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
              <Users className="w-4 h-4 mr-2 text-blue-500" /> Total Congregation Size
            </label>
            <Input 
              type="number" 
              value={congregationSize} 
              onChange={(e) => setCongregationSize(Number(e.target.value))}
              className="text-lg font-bold"
            />
            <p className="text-xs text-slate-400 mt-1">Average weekly attendance or total membership roll.</p>
          </div>

          <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
            <label className="text-sm font-semibold text-slate-500 uppercase flex justify-between mb-2">
              <span>Target Engagement Rate</span>
              <span className="font-bold text-slate-900 dark:text-white">{targetEngagementPercent}%</span>
            </label>
            <input 
              type="range" 
              min="10" 
              max="100" 
              value={targetEngagementPercent} 
              onChange={(e) => setTargetEngagementPercent(Number(e.target.value))}
              className="w-full accent-blue-600"
            />
            <p className="text-xs text-slate-400 mt-2">Percentage of congregation you want actively involved in small groups.</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100 dark:border-slate-800">
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Active Leaders</label>
              <Input 
                type="number" 
                value={activeLeaders} 
                onChange={(e) => setActiveLeaders(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Target Group Size</label>
              <Input 
                type="number" 
                value={targetGroupSize} 
                onChange={(e) => setTargetGroupSize(Number(e.target.value))}
              />
            </div>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          <Card className={cn("p-6 border-slate-200 dark:border-slate-800 shadow-xl text-white", model.isOverCapacity ? "bg-red-800" : "bg-emerald-800")}>
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-sm font-bold text-white/70 uppercase tracking-wider">Leadership Pipeline Gap</h3>
              {model.isOverCapacity ? <AlertCircle className="w-8 h-8 text-red-300" /> : <UserPlus className="w-8 h-8 text-emerald-300" />}
            </div>
            
            <div className="text-5xl font-black tracking-tight mb-2">
              {model.leadershipGap > 0 ? `Short ${model.leadershipGap} Leaders` : 'Fully Staffed'}
            </div>
            <p className="text-white/80 text-sm">
              {model.leadershipGap > 0 
                ? `You need to recruit ${model.leadershipGap} more small group leaders to hit your engagement target.`
                : 'You have enough leaders to support your engagement goals.'}
            </p>
          </Card>

          <Card className="p-0 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 overflow-hidden">
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              <div className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Engagement Target</div>
                  <div className="text-sm text-slate-500">{targetEngagementPercent}% of {congregationSize} attendees</div>
                </div>
                <div className="text-xl font-bold">
                  {model.targetParticipants.toLocaleString()} <span className="text-sm font-normal text-slate-400">people</span>
                </div>
              </div>
              
              <div className="p-4 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Current Capacity</div>
                  <div className="text-sm text-slate-500">{activeLeaders} leaders × {targetGroupSize} max size</div>
                </div>
                <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                  {model.currentCapacity.toLocaleString()} <span className="text-sm font-normal text-slate-400">slots</span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex justify-between text-xs font-bold uppercase tracking-wider mb-2">
                  <span className={cn(model.isOverCapacity ? "text-red-500" : "text-slate-500")}>Capacity Utilization</span>
                  <span className={cn(model.isOverCapacity ? "text-red-500" : "text-slate-900 dark:text-white")}>{model.capacityUtilization.toFixed(1)}%</span>
                </div>
                <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className={cn("h-full transition-all", model.capacityUtilization > 100 ? "bg-red-500" : model.capacityUtilization > 85 ? "bg-amber-500" : "bg-emerald-500")}
                    style={{ width: `${Math.min(100, model.capacityUtilization)}%` }}
                  />
                </div>
                {model.isOverCapacity && (
                  <p className="text-xs text-red-500 mt-2 font-semibold">Warning: Groups will be overcrowded, hurting intimacy and retention.</p>
                )}
              </div>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
